import { cva, VariantProps } from 'class-variance-authority';
import React, { HTMLAttributes, ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';

interface ICardProps extends HTMLAttributes<HTMLDivElement>, VariantProps<typeof cardVariants> {
    children?: ReactNode | string;
    className?: string;
    externalTo?: string;
}

interface ICardHeaderProps extends VariantProps<typeof cardHeaderVariants> {
    children?: ReactNode | string;
    className?: string;
}

interface ICardTitleProps {
    as?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
    children?: ReactNode | string;
    className?: string;
    variant?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
}

interface ICardBodyProps extends VariantProps<typeof cardBodyVariants> {
    children?: ReactNode | string;
    className?: string;
}

interface ICardFooterProps extends VariantProps<typeof cardFooterVariants> {
    children?: ReactNode | string;
    className?: string;
}

interface ICardMediaProps {
    children?: ReactNode | string;
    className?: string;
}

const cardVariants = cva(['card w-100 overflow-hidden'], {
    variants: {
        intent: {
            danger: '',
            info: '',
            neutral: '',
            none: '',
            primary: '',
            secondary: '',
            success: '',
            warning: '',
        },
        radius: {
            default: '',
        },
        variant: {
            outline: 'outline',
            solid: '',
        },
    },
});

const cardHeaderVariants = cva(['card-header'], {
    defaultVariants: {
        padding: 'base',
        divider: 'none',
    },
    variants: {
        divider: {
            none: '',
        },
        padding: {
            base: '',
        },
    },
});

const cardBodyVariants = cva(['card-body'], {
    defaultVariants: {
        padding: 'base',
    },
    variants: {
        padding: {
            base: '',
        },
    },
});

const cardFooterVariants = cva(['card-footer'], {
    defaultVariants: {
        padding: 'base',
        divider: 'none',
    },
    variants: {
        divider: {
            none: '',
        },

        padding: {
            base: '',
        },
    },
});

const Card = ({ children, className, intent, radius, variant, ...props }: ICardProps) => {
    return (
        <div
            className={twMerge(cardVariants({ className, intent, radius, variant }))}
            {...props}
        >
            {children}
        </div>
    );
};
const CardTitle = ({ as = 'h2', children, className, variant }: ICardTitleProps) => {
    return <>{React.createElement(as, { className: twMerge('mb-0', className, variant) }, children)}</>;
};

const CardHeader = ({ children, className, divider, padding }: ICardHeaderProps) => {
    return <div className={twMerge(cardHeaderVariants({ className, divider, padding }))}>{children}</div>;
};
const CardBody = ({ children, className, padding }: ICardBodyProps) => {
    return <div className={twMerge(cardBodyVariants({ className, padding }))}>{children}</div>;
};
const CardMedia = ({ children, className }: ICardMediaProps) => {
    return <div className={twMerge('', className)}>{children}</div>;
};
const CardFooter = ({ children, className, divider, padding }: ICardFooterProps) => {
    return <div className={twMerge(cardFooterVariants({ className, divider, padding }))}>{children}</div>;
};

export { Card, CardBody, CardFooter, CardHeader, CardMedia, CardTitle };
